import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Search, RotateCcw } from 'lucide-react';
import { PAYMENT_SORT_OPTIONS, PAYMENT_ORDER_OPTIONS } from '../models/PaymentModels';
import {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  zIndex,
} from '../styles/designTokens';

const SearchContainer = styled.div`
  background: ${colors.semantic.background.primary};
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[4]};
  margin-bottom: ${spacing[5]};
  box-shadow: ${shadows.sm};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${colors.primary[500]} 0%,
      ${colors.primary[700]} 100%
    );
    opacity: 0.8;
  }
`;

const SearchFlex = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

const SearchInputGroup = styled.div`
  flex: 1;
  min-width: 300px;
  position: relative;

  @media (max-width: 768px) {
    min-width: unset;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  flex-shrink: 0;

  @media (max-width: 768px) {
    justify-content: space-between;
    width: 100%;
  }
`;

const SearchInputContainer = styled.div`
  position: relative;

  .search-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: ${colors.semantic.text.disabled};
    z-index: ${zIndex.docked};
    width: 16px;
    height: 16px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 10px 10px 36px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.lg};
  font-size: ${typography.fontSize.sm};
  color: ${colors.semantic.text.secondary};
  background: ${colors.semantic.background.primary};
  transition: all 0.2s;
  height: 40px;
  font-family: ${typography.fontFamily.sans};

  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: ${colors.semantic.text.disabled};
    font-size: 13px;
  }
`;

const SortSelect = styled.select`
  min-width: 120px;
  padding: ${spacing[2]} 10px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  font-size: 13px;
  color: ${colors.semantic.text.secondary};
  background: ${colors.semantic.background.primary};
  cursor: pointer;
  transition: all 0.2s;
  height: 40px;
  font-family: ${typography.fontFamily.sans};

  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
  }

  @media (max-width: 768px) {
    min-width: 100px;
    flex: 1;
  }
`;

const SortGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    gap: ${spacing[2]};
  }
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid;
  border-radius: ${borderRadius.md};
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  ${props =>
    props.variant === 'primary'
      ? `
    background: ${colors.primary[500]};
    border-color: ${colors.primary[500]};
    color: ${colors.semantic.text.inverse};

    &:hover {
      background: ${colors.primary[600]};
      border-color: ${colors.primary[600]};
      transform: translateY(-1px);
    }

    &:active {
      background: ${colors.primary[700]};
      border-color: ${colors.primary[700]};
      transform: translateY(0);
    }
  `
      : `
    background: ${colors.semantic.background.primary};
    border-color: ${colors.neutral[300]};
    color: ${colors.semantic.text.tertiary};

    &:hover {
      background: ${colors.neutral[50]};
      border-color: ${colors.secondary[400]};
      color: ${colors.semantic.text.secondary};
      transform: translateY(-1px);
    }

    &:active {
      background: ${colors.neutral[100]};
      transform: translateY(0);
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Props 인터페이스 (검색 실행 시에만 API 호출 - 중복 호출 방지)
interface PaymentSearchBarProps {
  searchQuery: string;
  sortBy: string;
  order: 'asc' | 'desc';
  onSearchChange: (query: string) => void;
  onSortChange: (sortBy: string, order: 'asc' | 'desc') => void;
  onSearch: () => void;
  onReset: () => void;
  loading?: boolean;
}

// 결제 검색 바 컴포넌트
const PaymentSearchBar: React.FC<PaymentSearchBarProps> = ({
  searchQuery,
  sortBy,
  order,
  onSearchChange,
  onSortChange,
  onSearch,
  onReset,
  loading = false
}) => {
  // PaymentSearchBar 내부의 로컬 상태는 UI 입력 필드와 직접 바인딩됨
  // 이 값들은 사용자가 입력할 때마다 변경되고, 변경될 때마다 onSearchChange를 통해 부모에게 전달됨
  const [internalSearchQuery, setInternalSearchQuery] = useState(searchQuery);

  // 부모 컴포넌트(Payments.tsx)의 searchQuery prop이 변경되면 내부 상태도 동기화
  // (예: 필터 초기화 시)
  useEffect(() => {
    setInternalSearchQuery(searchQuery);
  }, [searchQuery]);

  // 검색 실행 핸들러: 부모의 onSearch (handleSearchSubmit)만 호출
  const handleSearchClick = () => {
    onSearch();
  };

  // 엔터 키 핸들러: 부모의 onSearch (handleSearchSubmit)만 호출
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  // 초기화 핸들러
  const handleReset = () => {
    setInternalSearchQuery('');
    onReset();
  };

  // 정렬 기준 변경 핸들러
  const handleSortFieldChange = (field: string) => {
    onSortChange(field, order);
  };

  // 정렬 순서 변경 핸들러
  const handleSortOrderChange = (newOrder: 'asc' | 'desc') => {
    onSortChange(sortBy, newOrder);
  };

  return (
    <SearchContainer>
      <SearchFlex>
        {/* 검색어 입력 */}
        <SearchInputGroup>
          <SearchInputContainer>
            <Search className="search-icon" />
            <SearchInput
              type="text"
              placeholder="결제상품명, 카드사, 유저명 등으로 검색..."
              value={internalSearchQuery}
              onChange={(e) => {
                setInternalSearchQuery(e.target.value);
                onSearchChange(e.target.value);
              }}
              onKeyPress={handleKeyPress}
              disabled={loading}
            />
          </SearchInputContainer>
        </SearchInputGroup>

        {/* 필터 그룹 */}
        <FilterGroup>
          {/* 정렬 그룹 - 두 개 드롭박스를 한 줄에 */}
          <SortGroup>
            <SortSelect
              value={sortBy}
              onChange={(e) => handleSortFieldChange(e.target.value)}
              disabled={loading}
            >
              {PAYMENT_SORT_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SortSelect>
            <SortSelect
              value={order}
              onChange={(e) => handleSortOrderChange(e.target.value as 'asc' | 'desc')}
              disabled={loading}
            >
              {PAYMENT_ORDER_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SortSelect>
          </SortGroup>

          {/* 액션 버튼들 */}
          <ActionButton
            variant="primary"
            onClick={handleSearchClick}
            disabled={loading}
            title="검색"
          >
            <Search />
          </ActionButton>
          <ActionButton
            variant="secondary"
            onClick={handleReset}
            disabled={loading}
            title="초기화"
          >
            <RotateCcw />
          </ActionButton>
        </FilterGroup>
      </SearchFlex>
    </SearchContainer>
  );
};

export default PaymentSearchBar; 